/**
 * voiceWrite — shared DOM write strategies for voice input.
 *
 * The Patient Registration Form is a React class component whose inputs are
 * mostly UNCONTROLLED and route through a single `handleChange` dispatcher
 * (plus `setDOB` and the country/state/city handlers). To fill a field by
 * voice WITHOUT touching any existing wiring, we set the DOM element's value
 * with React's own native value setter and dispatch the real input/change
 * event. React then runs the field's existing `onChange` exactly as if the
 * user had typed/selected — so validation, state mapping and formatting all
 * behave identically. This is the key to the "zero impact" guarantee.
 */

/** Set an <input>/<textarea> value so React's onChange (handleChange) fires. */
export function writeText(el, value) {
  if (!el) return false;
  const proto = el instanceof window.HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

/**
 * Join a freshly dictated chunk onto whatever the field already holds.
 *
 * The clinical free-text fields (diagnosis description, remarks, doctor
 * observations…) are built up over a consultation, so a second dictation must
 * never wipe the first — nor anything the doctor typed by hand in between.
 * Each chunk gets its own line by default, which is how these notes read back.
 */
export function appendText(existing, addition, separator = '\n') {
  // Trailing whitespace would otherwise turn into a blank line.
  const base = String(existing || '').replace(/\s+$/, '');
  const add = String(addition || '').trim();
  if (!add) return base;
  return base ? base + separator + add : add;
}

/** Set a <select> value (by option value) so React's onChange fires. */
export function writeSelect(el, value) {
  if (!el) return false;
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype, 'value',
  )?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/**
 * Find a form element by its `name` attribute, scoped to a container so the
 * Case.js and Dashboard.js copies of the form never collide. `container`
 * may be a DOM node or a ref-like `{ current }`; falls back to `document`.
 */
export function findByName(container, name) {
  const root = container && container.current ? container.current : container;
  const scope = root || document;
  return scope.querySelector(`[name="${name}"]`);
}
